/**
 * URL helpers — single source of truth for thumbnail & player URLs.
 * Handles both api.video (modern) and YouTube (legacy) video sources.
 * Priority: persisted thumbnail first, then apiVideoId, then YouTube sourceURL.
 */

import type { ContentFormat } from "./content.ts";

const YOUTUBE_REGEX = /(?:youtube\.com\/.*v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

type ThumbnailSource =
  | {
      content_format: "Video";
      thumbnail: { url: string } | null;
      apiVideoId: string | null;
      sourceURL: string | null;
    }
  | {
      content_format: Exclude<ContentFormat | "Article", "Video">;
      thumbnail: { url: string } | null;
    };

type VideoPlayerSource = {
  apiVideoId: string | null;
  sourceURL: string | null;
};

function extractYouTubeId(sourceURL: string): string | null {
  const match = sourceURL.match(YOUTUBE_REGEX);
  if (match === null) return null;
  const id = match[1];
  if (id === undefined) return null;
  return id;
}

export function apiVideoThumbnailUrl(videoId: string): string {
  return `https://vod.api.video/vod/${videoId}/thumbnail.jpg`;
}

export function apiVideoPlayerUrl(videoId: string): string {
  return `https://embed.api.video/vod/${videoId}`;
}

function youtubeThumbnailUrl(ytId: string): string {
  return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
}

export function thumbnailUrl(content: ThumbnailSource): string | null {
  if (content.thumbnail !== null) {
    return content.thumbnail.url;
  }
  if (content.content_format !== "Video") {
    return null;
  }
  if (content.apiVideoId !== null) {
    return apiVideoThumbnailUrl(content.apiVideoId);
  }
  if (content.sourceURL !== null) {
    const ytId = extractYouTubeId(content.sourceURL);
    if (ytId !== null) {
      return youtubeThumbnailUrl(ytId);
    }
  }
  return null;
}

export function videoPlayerUrl(video: VideoPlayerSource): string | null {
  if (video.apiVideoId !== null) {
    return apiVideoPlayerUrl(video.apiVideoId);
  }
  if (video.sourceURL !== null) {
    return video.sourceURL;
  }
  return null;
}
